import { Injectable } from '@angular/core';
import { jsPDF } from 'jspdf';
import { marked } from 'marked';

export interface MarkdownPdfConfig {
  pageWidth: number;
  pageHeight: number;
  margin: number;
  lineHeight: number;
}

export interface TextStyle {
  fontSize: number;
  fontStyle: 'normal' | 'bold' | 'italic';
  textColor: [number, number, number];
}

// Define custom types for marked tokens
interface MarkdownToken {
  type: string;
  text?: string;
  tokens?: MarkdownToken[];
  raw?: string;
  depth?: number;
  ordered?: boolean;
  items?: MarkdownToken[];
  header?: MarkdownToken[];
  rows?: MarkdownToken[][];
  lang?: string;
}

@Injectable({
  providedIn: 'root',
})
export class MarkdownToPdfService {
  private readonly defaultConfig: MarkdownPdfConfig = {
    pageWidth: 210, // A4 width in mm
    pageHeight: 297, // A4 height in mm
    margin: 20,
    lineHeight: 6,
  };

  private readonly styles = {
    h1: { fontSize: 18, fontStyle: 'bold' as const, textColor: [0, 0, 0] as [number, number, number] },
    h2: { fontSize: 16, fontStyle: 'bold' as const, textColor: [0, 0, 0] as [number, number, number] },
    h3: { fontSize: 14, fontStyle: 'bold' as const, textColor: [0, 0, 0] as [number, number, number] },
    h4: { fontSize: 12, fontStyle: 'bold' as const, textColor: [0, 0, 0] as [number, number, number] },
    h5: { fontSize: 11, fontStyle: 'bold' as const, textColor: [0, 0, 0] as [number, number, number] },
    h6: { fontSize: 10, fontStyle: 'bold' as const, textColor: [0, 0, 0] as [number, number, number] },
    normal: { fontSize: 10, fontStyle: 'normal' as const, textColor: [0, 0, 0] as [number, number, number] },
    bold: { fontSize: 10, fontStyle: 'bold' as const, textColor: [0, 0, 0] as [number, number, number] },
    italic: { fontSize: 10, fontStyle: 'italic' as const, textColor: [0, 0, 0] as [number, number, number] },
    code: { fontSize: 9, fontStyle: 'normal' as const, textColor: [60, 60, 60] as [number, number, number] },
    codeBlock: { fontSize: 8, fontStyle: 'normal' as const, textColor: [40, 40, 40] as [number, number, number] },
  };

  constructor() {}

  public addMarkdownToPdf(pdf: jsPDF, markdownText: string, yPosition: number, config?: Partial<MarkdownPdfConfig>): number {
    const mergedConfig = { ...this.defaultConfig, ...config };

    // Configure marked options
    const renderer = new marked.Renderer();
    marked.setOptions({
      breaks: true,
      gfm: true,
    });

    // Parse markdown to tokens
    const tokens = marked.lexer(markdownText) as MarkdownToken[];

    return this.renderTokensToPdf(pdf, tokens, yPosition, mergedConfig);
  }

  private renderTokensToPdf(pdf: jsPDF, tokens: MarkdownToken[], yPosition: number, config: MarkdownPdfConfig): number {
    let currentY = yPosition;

    for (const token of tokens) {
      // Check if we need a new page
      if (currentY > config.pageHeight - config.margin - 20) {
        pdf.addPage();
        currentY = config.margin;
      }

      currentY = this.renderToken(pdf, token, currentY, config);
    }

    return currentY;
  }

  private renderToken(pdf: jsPDF, token: MarkdownToken, yPosition: number, config: MarkdownPdfConfig): number {
    let currentY = yPosition;

    switch (token.type) {
      case 'heading':
        currentY = this.renderHeading(pdf, token, currentY, config);
        break;
      case 'paragraph':
        currentY = this.renderParagraph(pdf, token, currentY, config);
        break;
      case 'list':
        currentY = this.renderList(pdf, token, currentY, config);
        break;
      case 'code':
        currentY = this.renderCodeBlock(pdf, token, currentY, config);
        break;
      case 'table':
        currentY = this.renderTable(pdf, token, currentY, config);
        break;
      case 'blockquote':
        currentY = this.renderBlockquote(pdf, token, currentY, config);
        break;
      case 'hr':
        currentY = this.renderHorizontalRule(pdf, currentY, config);
        break;
      default:
        // Handle other token types as plain text
        if (token.text) {
          currentY = this.renderPlainText(pdf, token.text, currentY, config, this.styles.normal);
        }
        break;
    }

    return currentY + 2; // Add small spacing between elements
  }

  private renderHeading(pdf: jsPDF, token: MarkdownToken, yPosition: number, config: MarkdownPdfConfig): number {
    const depth = token.depth || 1;
    const style = this.styles[`h${depth}` as keyof typeof this.styles] || this.styles.h6;

    // Add some spacing before headings (except h1)
    let currentY = yPosition + (depth > 1 ? 4 : 2);

    this.applyTextStyle(pdf, style);

    const text = this.extractPlainText(token.text || '');
    const lines = pdf.splitTextToSize(text, config.pageWidth - config.margin * 2);

    pdf.text(lines, config.margin, currentY);

    return currentY + lines.length * config.lineHeight + 2;
  }

  private renderParagraph(pdf: jsPDF, token: MarkdownToken, yPosition: number, config: MarkdownPdfConfig): number {
    let currentY = yPosition + 2;

    // Handle inline formatting within paragraphs
    if (token.tokens) {
      currentY = this.renderInlineTokens(pdf, token.tokens, currentY, config);
    } else {
      const text = this.extractPlainText(token.text || '');
      currentY = this.renderPlainText(pdf, text, currentY, config, this.styles.normal);
    }

    return currentY + 2;
  }

  private renderInlineTokens(pdf: jsPDF, tokens: MarkdownToken[], yPosition: number, config: MarkdownPdfConfig): number {
    let currentY = yPosition;
    let currentLine = '';
    let currentLineWidth = 0;
    const maxWidth = config.pageWidth - config.margin * 2;

    for (const token of tokens) {
      let text = '';
      let style: TextStyle = this.styles.normal;

      switch (token.type) {
        case 'text':
          text = token.text || '';
          break;
        case 'strong':
          text = this.extractPlainText(token.text || '');
          style = this.styles.bold;
          break;
        case 'em':
          text = this.extractPlainText(token.text || '');
          style = this.styles.italic;
          break;
        case 'codespan':
          text = `\`${token.text || ''}\``;
          style = this.styles.code;
          break;
        case 'link':
          text = this.extractPlainText(token.text || '');
          style = { ...this.styles.normal, textColor: [0, 0, 255] as [number, number, number] };
          break;
        default:
          if (token.text) {
            text = this.extractPlainText(token.text);
          }
          break;
      }

      // Check if adding this text would exceed line width
      this.applyTextStyle(pdf, style);
      const textWidth = pdf.getTextWidth(text);

      if (currentLineWidth + textWidth > maxWidth && currentLine.length > 0) {
        // Render current line and start new line
        pdf.text(currentLine, config.margin, currentY);
        currentY += config.lineHeight;
        currentLine = text;
        currentLineWidth = textWidth;
      } else {
        currentLine += text;
        currentLineWidth += textWidth;
      }
    }

    // Render remaining text
    if (currentLine.length > 0) {
      this.applyTextStyle(pdf, this.styles.normal);
      pdf.text(currentLine, config.margin, currentY);
      currentY += config.lineHeight;
    }

    return currentY;
  }

  private renderList(pdf: jsPDF, token: MarkdownToken, yPosition: number, config: MarkdownPdfConfig): number {
    let currentY = yPosition + 2;

    if (token.items) {
      for (let i = 0; i < token.items.length; i++) {
        const item = token.items[i];
        const bullet = token.ordered ? `${i + 1}. ` : '• ';

        this.applyTextStyle(pdf, this.styles.normal);
        pdf.text(bullet, config.margin, currentY);

        const bulletWidth = pdf.getTextWidth(bullet);
        const itemText = this.extractPlainText(item.text || '');
        const lines = pdf.splitTextToSize(itemText, config.pageWidth - config.margin * 2 - bulletWidth);

        pdf.text(lines, config.margin + bulletWidth, currentY);
        currentY += lines.length * config.lineHeight + 1;
      }
    }

    return currentY + 2;
  }

  private renderCodeBlock(pdf: jsPDF, token: MarkdownToken, yPosition: number, config: MarkdownPdfConfig): number {
    let currentY = yPosition + 2;

    const codeText = token.text || '';

    // Draw background rectangle for code block
    pdf.setFillColor(245, 245, 245);
    const codeHeight = codeText.split('\n').length * config.lineHeight + 6;
    pdf.rect(config.margin - 2, currentY - 3, config.pageWidth - config.margin * 2 + 4, codeHeight, 'F');

    this.applyTextStyle(pdf, this.styles.codeBlock);

    const lines = codeText.split('\n');
    for (const line of lines) {
      pdf.text(line, config.margin, currentY);
      currentY += config.lineHeight;
    }

    return currentY + 4;
  }

  private renderTable(pdf: jsPDF, token: MarkdownToken, yPosition: number, config: MarkdownPdfConfig): number {
    let currentY = yPosition + 4;
    const maxWidth = config.pageWidth - config.margin * 2;

    if (!token.header || !token.rows) {
      return currentY;
    }

    const colWidth = maxWidth / token.header.length;

    // Render header
    this.applyTextStyle(pdf, this.styles.bold);
    for (let i = 0; i < token.header.length; i++) {
      const headerText = this.extractPlainText(token.header[i].text || '');
      const x = config.margin + i * colWidth;
      pdf.text(headerText, x, currentY);
    }
    currentY += config.lineHeight + 2;

    // Draw header line
    pdf.setDrawColor(200, 200, 200);
    pdf.line(config.margin, currentY - 1, config.margin + maxWidth, currentY - 1);

    // Render rows
    this.applyTextStyle(pdf, this.styles.normal);
    for (const row of token.rows) {
      for (let i = 0; i < row.length; i++) {
        const cellText = this.extractPlainText(row[i].text || '');
        const x = config.margin + i * colWidth;
        pdf.text(cellText, x, currentY);
      }
      currentY += config.lineHeight + 1;
    }

    return currentY + 4;
  }

  private renderBlockquote(pdf: jsPDF, token: MarkdownToken, yPosition: number, config: MarkdownPdfConfig): number {
    let currentY = yPosition + 2;

    // Draw left border for blockquote
    pdf.setDrawColor(180, 180, 180);
    pdf.setLineWidth(2);
    const quoteHeight = 20; // Estimate height
    pdf.line(config.margin, currentY, config.margin, currentY + quoteHeight);

    // Render blockquote content with indentation
    this.applyTextStyle(pdf, this.styles.italic);
    if (token.tokens) {
      for (const subToken of token.tokens) {
        currentY = this.renderToken(pdf, subToken, currentY, {
          ...config,
          margin: config.margin + 10,
        });
      }
    }

    return currentY + 2;
  }

  private renderHorizontalRule(pdf: jsPDF, yPosition: number, config: MarkdownPdfConfig): number {
    const currentY = yPosition + 4;

    pdf.setDrawColor(200, 200, 200);
    pdf.setLineWidth(0.5);
    pdf.line(config.margin, currentY, config.pageWidth - config.margin, currentY);

    return currentY + 4;
  }

  private renderPlainText(pdf: jsPDF, text: string, yPosition: number, config: MarkdownPdfConfig, style: TextStyle): number {
    this.applyTextStyle(pdf, style);

    const lines = pdf.splitTextToSize(text, config.pageWidth - config.margin * 2);
    pdf.text(lines, config.margin, yPosition);

    return yPosition + lines.length * config.lineHeight;
  }

  private applyTextStyle(pdf: jsPDF, style: TextStyle): void {
    pdf.setFontSize(style.fontSize);
    pdf.setFont('helvetica', style.fontStyle);
    pdf.setTextColor(style.textColor[0], style.textColor[1], style.textColor[2]);
  }

  private extractPlainText(text: string | MarkdownToken[] | MarkdownToken): string {
    if (typeof text === 'string') {
      return text;
    }

    if (Array.isArray(text)) {
      return text.map((token) => this.extractPlainText(token)).join('');
    }

    if (text && typeof text === 'object' && 'text' in text) {
      return this.extractPlainText(text.text || '');
    }

    return '';
  }
}
